package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.SessionState
import dev.lingvo.site.ds.LAvatar
import dev.lingvo.site.ds.LAvatarSize
import dev.lingvo.site.ds.LChevronDownIcon
import dev.lingvo.site.ds.LHelpIcon
import dev.lingvo.site.ds.LKeyIcon
import dev.lingvo.site.ds.LLogoutIcon
import dev.lingvo.site.ds.LMailIcon
import dev.lingvo.site.ds.LMenu
import dev.lingvo.site.ds.LMenuItem
import dev.lingvo.site.ds.LMoonIcon
import dev.lingvo.site.ds.LSearchField
import dev.lingvo.site.ds.LSettingsIcon
import dev.lingvo.site.ds.LSunIcon
import dev.lingvo.site.ds.LUserIcon
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.commandChord
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.state.ProjectDirectory
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import androidx.compose.runtime.setValue
import androidx.compose.runtime.getValue
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.iconSize

val AppAvatarTriggerStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(colorMode.toSitePalette().surface)
            .color(colorMode.toSitePalette().textPrimary)
            // **`borderStrong`, never `border`.** `border` is 1.41:1 against the surface it sits on
            // — the token's own KDoc calls it a divider, never a control boundary — so the pill's
            // edge was invisible to anyone the 3:1 non-text floor exists for, and the trigger read
            // as loose text rather than as something pressable.
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Pill)
            .padding(Space.XXS)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", Space.SM.toString())
                property("cursor", "pointer")
            }
    }
    // The paper's second step, not pure white: `surfaceRaised` is reserved for things that float.
    cssRule(":hover") { Modifier.backgroundColor(colorMode.toSitePalette().surfaceSunken) }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

@Composable
internal fun AppTopBar(currentPath: String) {
    val slug = projectSlug(currentPath)
    // **The `/` cap is a promise, so the key is bound here.** The field was passing
    // `shortcutHint = null` to switch a working affordance off; restoring the cap without the
    // binding would have been worse than leaving it off, because a rendered `LKbd` is the product
    // telling somebody a key works.
    //
    // The guard is the whole of it: `/` is a character somebody is often in the middle of typing,
    // and stealing focus out of a text field mid-word is the failure that makes people stop trusting
    // single-key shortcuts anywhere in a product.
    DisposableEffect(Unit) {
        val focusSearch: (Event) -> Unit = { event ->
            if ((event as? KeyboardEvent)?.key == "/") {
                val active = document.activeElement
                val tag = active?.tagName?.lowercase()
                val typing = tag == "input" || tag == "textarea" || tag == "select" ||
                    (active as? HTMLElement)?.isContentEditable == true
                if (!typing) {
                    (document.querySelector("[$SEARCH_FIELD_ATTR] input") as? HTMLElement)?.let {
                        // Or the slash lands in the field it just focused.
                        event.preventDefault()
                        it.focus()
                    }
                }
            }
        }
        document.addEventListener("keydown", focusSearch)
        onDispose { document.removeEventListener("keydown", focusSearch) }
    }
    Row(
        // The gutters live in `AppTopBarStyle` now, because below MD they are not a matter of taste:
        // the compact navigation trigger is fixed at `left: 8` and 40 wide, so a 32px gutter put the
        // first crumb under it. See the style.
        AppTopBarStyle.toModifier(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // The trail, not breadcrumbs of URL segments. It says what the page acts on — workspace,
        // then project, then section — with the project's *name* and languages spelled out rather
        // than `my-app` and `De`.
        //
        // **Above MD only.** At 390 the bar tried to hold trail, a 420px search field and the
        // avatar; what survived was a leading separator with no first crumb and `Acti…` after it,
        // beside a field squeezed to about 130px whose placeholder clipped to `Sear`. A truncated
        // crumb says nothing the drawer does not already say with full labels, and giving the width
        // back is what makes the one control on that bar usable.
        Div(Modifier.displayIfAtLeast(Breakpoint.MD).flexShrink(1).minWidth(0.px).toAttrs()) {
            AppTrail(currentPath)
        }
        Spacer()
        // **The field *is* the palette now.** It used to be a second search: it routed a query to
        // the project's keys page or to the project list, which is one of the two things the palette
        // already does and neither of the two it does better. Two search surfaces on one screen, one
        // of them able to run actions and one not, is a choice nobody should have to make — so the
        // field keeps its shape, its `/` binding and its place in the bar, and hands what was typed
        // to the palette with the keystroke already in it.
        //
        // It holds no value of its own, because the palette's own field owns the query from the
        // first character onward. A field that kept a second copy would show the query twice and
        // then disagree with itself the moment either one was edited.
        // Every sentence the bar would accept, longest first; the field measures itself and takes
        // the longest that fits. The bar used to make this choice at a 900px media query, which is
        // a guess about a width it does not have — see [topBarSearchPlaceholders].
        val sentences = topBarSearchPlaceholders(
            slug = slug,
            global = t("app_shell_chrome.search_or_run_a_command", "Search or run a command"),
            shortest = t("app_shell_chrome.search", "Search"),
        )
        LSearchField(
            value = "",
            onValueChange = { CommandPaletteState.request(it) },
            modifier = Modifier.flexGrow(1).maxWidth(420.px).attr(SEARCH_FIELD_ATTR, ""),
            label = t("app_shell_chrome.search_and_commands", "Search and commands"),
            // The placeholder names what the surface behind it can do, not only what it can find:
            // the rows it opens include starting a run and fixing the first QA error, and a
            // placeholder reading "Search projects" hides every one of them.
            placeholder = sentences.first(),
            shorterPlaceholders = sentences.drop(1),
            // The chord that opens the same surface, on the control that opens it. It used to be a
            // separate 32px chip beside the theme toggle — a fourth thing in a bar the spec gives
            // three — announcing a shortcut for a surface nothing else in the bar mentioned.
            shortcutHint = commandChord("K"),
            onCommit = { CommandPaletteState.request() },
        )
        Row(
            // `flex-shrink: 0`. These are fixed-size controls with nothing inside them that can
            // give. Left shrinkable, this row was squeezed to 133px around ~190px of content at a
            // 900px viewport and the account pill simply hung out of it, putting the whole document
            // into horizontal scroll. The search field beside it is the elastic one and yields
            // instead.
            Modifier.flexShrink(0).padding(left = Space.MD).gap(Space.SM),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AvatarMenu()
        }
    }
}

@Composable
private fun AvatarMenu() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val user = (Session.state as? SessionState.Authenticated)?.user
    val name = user?.name.orEmpty().ifBlank { t("app_shell_chrome.user", "User") }
    var expanded by remember { mutableStateOf(false) }
    // **The theme toggle lives here now.** It was a fourth control in a bar the spec gives three,
    // and it is the one control in the shell nobody presses twice a day — a preference, beside the
    // other preferences, rather than a permanent neighbour of the account pill.
    var colorMode by ColorMode.currentState
    // **Six rows, not three.** `Your account`, `Security` and `Notifications` are finished pages
    // that nothing in the shell linked to — reachable only by typing their URLs — so the one region
    // of the app that is identical for every role, and the place a translator's "what can I
    // actually change here" instinct lands, offered them nothing but tokens and help.
    //
    // Every destination here is VIEWER-minimum and own-only, which is why the list takes no role.
    val items = listOf(
        LMenuItem(
            user?.email.orEmpty().ifBlank { t("app_shell_chrome.signed_in", "Signed in") },
            disabled = true,
            icon = { LUserIcon() },
        ),
        LMenuItem(t("app_shell_chrome.your_account", "Your account"), icon = { LUserIcon() }),
        LMenuItem(t("app_shell_chrome.security", "Security"), icon = { LKeyIcon() }),
        LMenuItem(t("app_shell_chrome.notifications", "Notifications"), icon = { LMailIcon() }),
        LMenuItem(t("app_shell_chrome.api_tokens", "API tokens"), icon = { LSettingsIcon() }),
        // The literal copy: the page lists the shell's two shortcuts as well as answering
        // questions, and `Help` alone hid half of what is behind the row.
        LMenuItem(t("app_shell_chrome.help_and_shortcuts", "Help and shortcuts"), icon = { LHelpIcon() }),
        // Both forms written out rather than chosen by a ternary inside one lookup: an argument
        // position no extractor run can reach is how the one control that is always on screen came
        // to be English in every locale.
        LMenuItem(
            if (colorMode.isDark) {
                t("app_shell_chrome.switch_to_light_theme", "Switch to light theme")
            } else {
                t("app_shell_chrome.switch_to_dark_theme", "Switch to dark theme")
            },
            icon = { if (colorMode.isDark) LSunIcon() else LMoonIcon() },
        ),
        LMenuItem(t("app_shell_chrome.log_out", "Log out"), icon = { LLogoutIcon() }),
    )
    LMenu(
        expanded = expanded,
        items = items,
        onExpandedChange = { expanded = it },
        onSelect = { index ->
            when (index) {
                1 -> ctx.router.tryRoutingTo("/app/account")
                2 -> ctx.router.tryRoutingTo("/app/account/security")
                3 -> ctx.router.tryRoutingTo("/app/account/notifications")
                4 -> ctx.router.tryRoutingTo("/app/settings/tokens")
                5 -> ctx.router.tryRoutingTo("/app/help")
                // **Log out has to end the session on the server, not only forget it here.**
                // `Session.logout()` clears the stored token and sets `LoggedOut`, and that is all
                // it does — no request leaves the browser. So the bearer token this tab dropped
                // stayed valid for its full life: a copy of it taken from local storage kept
                // working, and the device stayed listed on the account's own Security page as
                // though somebody were still signed in on it. The endpoint that fixes this has
                // existed all along — `SessionRevocationTest` asserts the revoked token 401s on the
                // next request — and the one Log out in the product was not calling it.
                //
                // Awaited rather than fired and forgotten: the request carries the very token
                // `logout()` is about to clear, and this scope dies with the shell the moment the
                // session state flips. Bounded, because a hanging revoke must never be the reason
                // somebody walks away from a shared machine still signed in — the local half runs
                // whatever the server does or fails to do.
                6 -> colorMode = colorMode.opposite
                7 -> scope.launch {
                    try {
                        withTimeoutOrNull(REVOKE_TIMEOUT_MS) {
                            ApiClient.sessions().firstOrNull { it.current }
                                ?.let { ApiClient.revokeSession(it.id) }
                        }
                    } catch (cancelled: CancellationException) {
                        throw cancelled
                    } catch (failure: Throwable) {
                        // Nothing to report and nothing to decide. The session is ending either
                        // way, and a server that cannot be reached is not a reason to leave
                        // somebody signed in on the machine in front of them.
                    }
                    Session.logout()
                    ctx.router.tryRoutingTo("/login", UpdateHistoryMode.REPLACE)
                }
            }
        },
    ) { toggle, triggerModifier ->
        Button(
            AppAvatarTriggerStyle.toModifier()
                .then(triggerModifier)
                .toAttrs {
                    type(ButtonType.Button)
                    // Names whose account it is: below 900 the pill is an avatar alone, and
                    // "Open account menu" on a circle of initials tells a screen-reader user
                    // nothing the initials were already failing to tell everyone else.
                    attr("aria-label", trf("app_shell_chrome.account_menu", "Account menu, {name}", "name" to name))
                    onClick { toggle() }
                },
        ) {
            LAvatar(name = name, size = LAvatarSize.MD)
            Span(
                Modifier
                    .displayIfAtLeast(Breakpoint.MD)
                    .padding(leftRight = Space.XS)
                    .fontSize(TypeScale.Small)
                    .fontWeight(600)
                    .toAttrs(),
            ) {
                Text(name)
            }
            LChevronDownIcon(Modifier.iconSize(IconSize.MD).displayIfAtLeast(Breakpoint.MD))
        }
    }
}

/**
 * How long Log out waits for the server before ending the session locally anyway.
 *
 * Long enough for an ordinary round trip on a bad connection, short enough that nobody stands in
 * front of a machine wondering whether the button worked.
 */
private const val REVOKE_TIMEOUT_MS = 4000L

/**
 * The handle the `/` binding finds the field by.
 *
 * **An attribute, not the accessible name.** The selector used to match the input by its English
 * `aria-label`, so the field's own name could not be translated without silently killing the
 * shortcut in every locale that translated it — in a product whose argument is that its interface
 * is translatable. An attribute nothing renders cannot be localized by accident, and the name above
 * is now free to be a catalogue lookup like every other string in the shell.
 *
 * It lands on the field's wrapper rather than its `<input>`, because `LSearchField` puts a caller's
 * modifier on the row; the `input` in the selector is the one input inside it.
 */
internal const val SEARCH_FIELD_ATTR = "data-lingvo-search"

/**
 * Every sentence the bar's field may show, longest first.
 *
 * The scoped one first: a field that says "Search projects" and then submits to one project's keys
 * is lying about where the query goes, which is worse than naming the project by its slug for the
 * moment before the directory lands — so the fallback inside the scoped sentence is the slug, on
 * the same rule the trail uses.
 *
 * **The bar offers, the field chooses.** This used to end in a boolean the bar computed from a
 * 900px media query, which is a guess about a width the bar does not have: at 390 its own gutters,
 * the account pill and the field's icon leave the input around 250px, and the re-shoot found even
 * the workspace-wide sentence clipped — "Search or run a comma…" on activity, keys, data-jobs,
 * review, usage and agents. A threshold picked in advance cannot know that, and cannot know it for
 * a translated sentence either. So all three rungs go to the field, which measures the box it is
 * actually in. See `LSearchField`'s `shorterPlaceholders`.
 *
 * The last rung is a word rather than a sentence on purpose. It says less than the two above it and
 * is meant to: a control that names one of the two things it does is still honest, and a control
 * whose name is cut through a glyph is not.
 *
 * @param global the workspace-wide sentence and [shortest] the one-word floor, both read from the
 *   catalogue by the caller because this is not composition.
 */
internal fun topBarSearchPlaceholders(slug: String?, global: String, shortest: String): List<String> =
    buildList {
        if (slug != null) {
            add(
                trf(
                    "app_shell_chrome.search_project_or_run_a_command",
                    "Search {project} or run a command",
                    "project" to (ProjectDirectory.find(slug)?.name ?: slug),
                ),
            )
        }
        add(global)
        add(shortest)
    }.distinct()

private fun projectSlug(path: String): String? {
    val segments = path.split('/').filter(String::isNotBlank)
    val projectsIndex = segments.indexOf("projects")
    if (projectsIndex < 0) return null
    return segments.getOrNull(projectsIndex + 1)?.takeIf { it != "new" }
}

